import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMount } from 'react-use'
import ActivityLogPanel from 'src/components/molecules/ActivityLogPanel'
import { Path as BreadcrumbsPath } from 'src/components/molecules/Breadcrumbs'
import Loading from 'src/components/templates/Loading'
import { AuthContext } from 'src/context/auth.context'
import { ToastTriggerContext } from 'src/context/toast.context'
import {
  ActivityLogEntityType,
  EVENT_TYPE_LABELS,
} from 'src/domain/activityLog'
import { isActivityLogEnabled } from 'src/domain/org'
import { AdminAdminStoreManagerRoles, isOneOfRoles } from 'src/domain/role'
import { useActivityLogPanel } from 'src/hooks/useActivityLogPanel'
import { useApiMember } from 'src/hooks/useApiMember'
import useAppTitle from 'src/hooks/useAppTitle'
import Path, { OrgPathParams } from 'src/routes/path'
import {
  ActivityLogScopeTypeEnum,
  Member,
  RoleEnum,
  Store as EntityStore,
} from 'src/slices/services/api'
import {
  getStores,
  selectStoresByParams,
  selectStoresRequestStateByParams,
} from 'src/slices/stores/storesSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import TemplatesMembersList from '../../templates/List'
import { adjustMembers } from '../Update/presenter'

const PagesMembersList: React.FC = (): ReactElement => {
  useAppTitle('ユーザー管理')
  const { getMembers } = useApiMember()
  const authContext = useContext(AuthContext)
  const toastContex = useContext(ToastTriggerContext)
  const [members, setMembers] = useState<Member[]>([])
  const [isLoaded, setLoaded] = useState<boolean>(false)
  const { orgCode } = useParams<OrgPathParams>() as OrgPathParams
  const navigate = useNavigate()
  const param = useMemo(() => {
    return { orgCode, page: 1, perPage: 1000 }
  }, [orgCode])

  const dispatch = useAppDispatch()
  const stores: EntityStore[] =
    useAppSelector((state) => selectStoresByParams(param)(state)) ?? []

  const { status } = useAppSelector((state) =>
    selectStoresRequestStateByParams(param)(state)
  )

  const selfRole: RoleEnum | undefined = useAppSelector(
    (state) => state.auth.currentAuthorization?.role
  )
  const currentAuthorization = useAppSelector(
    (state) => state.auth.currentAuthorization
  )

  const {
    isOpen: isLogPanelOpen,
    open: openLogPanel,
    close: closeLogPanel,
    logs: activityLogs,
    isLoading: isActivityLogsLoading,
    isLoadingMore: isActivityLogsLoadingMore,
    hasMore: hasMoreActivityLogs,
    dateRangeFilter: activityLogDateRangeFilter,
    errorMessage: activityLogError,
    onLoadMore: handleLoadMoreActivityLogs,
  } = useActivityLogPanel({
    orgCode,
    entityType: ActivityLogEntityType.Member,
    scopeType: ActivityLogScopeTypeEnum.Organization,
    scopeId: currentAuthorization?.organization.id,
  })

  useEffect(() => {
    if (status === 'idle') {
      dispatch(getStores(param))
    }
  }, [dispatch, status, param])

  const breadCrumbPaths: BreadcrumbsPath[] = [
    { index: 0, title: 'ユーザー管理', to: Path.members(orgCode) },
  ]

  useMount(async () => {
    const result = await getMembers(orgCode)
    if (!result.success) {
      toastContex.sendToast({
        variant: 'error',
        title: `ユーザーの取得に失敗しました: ${result.message}`,
      })
      return
    }
    const selfEmail = authContext.getUserEmail()
    if (!selfRole || !selfEmail) {
      return
    }
    const adjustedMembers = adjustMembers(
      result.data || [],
      selfEmail,
      selfRole
    )
    if (!adjustedMembers) {
      toastContex.sendToast({
        variant: 'error',
        title: `再度ログインしなおしてからお試しください`,
      })
    }
    setMembers(adjustedMembers || [])
    setLoaded(true)
  })

  const onClickUpdateMember = () => {
    navigate(Path.membersUpdate(orgCode))
  }

  const onClickCreateMember = () => {
    navigate(Path.membersCreate(orgCode))
  }

  const showSwitchByRole = useCallback((): boolean => {
    return isOneOfRoles(AdminAdminStoreManagerRoles, selfRole)
  }, [selfRole])

  const handleOpenLogPanel = useCallback(() => {
    if (!currentAuthorization?.organization.id) {
      toastContex.sendToast({
        variant: 'error',
        title: '組織情報の取得に失敗しました',
      })
      return
    }
    openLogPanel()
  }, [currentAuthorization?.organization.id, toastContex, openLogPanel])

  return (
    <div>
      {!showSwitchByRole() && <div>権限がありません</div>}
      {isLoaded && status === 'succeeded' && showSwitchByRole() ? (
        <TemplatesMembersList
          orgCode={orgCode}
          breadCrumbPaths={breadCrumbPaths}
          members={members}
          stores={stores}
          role={selfRole}
          onClickCreateMember={onClickCreateMember}
          onClickUpdateMember={onClickUpdateMember}
          onClickHistoryButton={
            isActivityLogEnabled(orgCode) ? handleOpenLogPanel : undefined
          }
        />
      ) : (
        <Loading />
      )}
      <ActivityLogPanel
        orgCode={orgCode}
        open={isLogPanelOpen}
        title="変更履歴"
        logs={activityLogs}
        isLoading={isActivityLogsLoading}
        isLoadingMore={isActivityLogsLoadingMore}
        hasMore={hasMoreActivityLogs}
        eventTypeLabels={EVENT_TYPE_LABELS}
        dateRangeFilter={activityLogDateRangeFilter}
        errorMessage={activityLogError}
        onClose={closeLogPanel}
        onLoadMore={handleLoadMoreActivityLogs}
      />
    </div>
  )
}

export default PagesMembersList
